import { useState } from 'react';
import { parseDelimited } from '@/utils/csv-helper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, FileUp, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '@/api/client';

const SAMPLE = `name,code,brand,orderType,serviceType,volumetricDivisor,tatDays,maxLiability,addressLabelName,weightCategory,isAggregator,isActive,contactName,contactEmail,contactPhone
Delhivery Surface 5KG,delhivery_s5,Delhivery,B2C,SURFACE,5000,3,25000,Delhivery Surface,5KG,false,true,,,
Delhivery Express,delhivery_exp,Delhivery,B2C,EXPRESS,5000,2,25000,Delhivery Express,2KG,false,true,,,
DTDC Noida,dtdc_noida,DTDC,B2C,SURFACE,5000,4,15000,DTDC Surface,5KG,false,true,,,`;

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

/** Courier import rows, via the shared quote-aware tokeniser (see utils/csv-helper). */
function parseCsv(text: string): Array<Record<string, string>> {
  return parseDelimited(text, { mapHeader: (h) => h.trim() }).rows;
}


export function ImportCourierDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  const importMut = useMutation({
    mutationFn: async ({ rows, dryRun }: { rows: Array<Record<string, string>>; dryRun: boolean }) => {
      const r = await api.post<ImportResult>('/couriers/import', { rows, dryRun });
      return r.data;
    },
    onSuccess: (data, vars) => {
      setResult(data);
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['couriers'] });
        toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Import failed'),
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(String(ev.target?.result ?? ''));
    reader.readAsText(file);
  };

  const run = (dryRun: boolean) => {
    const rows = parseCsv(text);
    if (rows.length === 0) { toast.error('No rows to import'); return; }
    importMut.mutate({ rows, dryRun });
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
        <div className="font-medium text-slate-700 mb-1">CSV format</div>
        Columns: <code className="text-[10px]">name, code, brand, orderType, serviceType, volumetricDivisor, tatDays, maxLiability, addressLabelName, weightCategory, isAggregator, isActive, contactName, contactEmail, contactPhone</code>
        <button
          type="button"
          onClick={() => setText(SAMPLE)}
          className="block mt-2 text-primary text-xs underline hover:no-underline">
          Load sample data
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Upload CSV file</label>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          className="text-sm w-full file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:text-sm file:bg-primary file:text-white file:cursor-pointer cursor-pointer" />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Or paste CSV content</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="name,code,brand,..."
          className="input font-mono text-xs" />
      </div>

      {result && (
        <div className={`rounded-lg p-3 text-sm ${result.errors.length === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-2 font-medium">
            {result.errors.length === 0
              ? <><CheckCircle2 size={16} className="text-green-600" /> <span className="text-green-800">Success</span></>
              : <><AlertCircle size={16} className="text-amber-600" /> <span className="text-amber-800">With issues</span></>}
          </div>
          <div className="mt-1 text-slate-700">
            Created: <b>{result.created}</b> · Updated: <b>{result.updated}</b> · Skipped: <b>{result.skipped}</b>
          </div>
          {result.errors.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-xs text-amber-800 max-h-32 overflow-y-auto">
              {result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
              {result.errors.length > 10 && <li>… +{result.errors.length - 10} more</li>}
            </ul>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Close</button>
        <button
          type="button"
          onClick={() => run(true)}
          disabled={importMut.isPending || !text.trim()}
          className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2">
          {importMut.isPending && <Loader2 size={14} className="animate-spin" />}
          Validate
        </button>
        <button
          type="button"
          onClick={() => run(false)}
          disabled={importMut.isPending || !text.trim()}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {importMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
          Import
        </button>
      </div>
    </div>
  );
}
